import React, { ReactNode, useContext } from "react";
import {
  WhmBox,
  WhmButton,
  WhmDropzone,
  WhmStack,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { I18nContext } from "../../../contexts/I18nContext";
import translationKeys from "../../../contexts/I18nContext/translationKeys";
import TranslationKeys from "../../../contexts/I18nContext/translationKeys";

interface DocumentUploaderProps {
  title: ReactNode;
  subtitle: ReactNode;
  errorMessage?: string;
  documentType: string;
  onUpload: (file: File, documentType: string) => Promise<void>;
  onDelete: () => Promise<void>;
  fileUrl?: string;
  fileName?: string;
  disabled?: boolean;
  /**
   * Operator's comment when this ALREADY-UPLOADED document was flagged for
   * replacement. WhmDropzone's uploaded-file view has no slot for this (its
   * isErrored/uploadingErrorText only apply to the empty/upload-in-progress
   * state), so when this is set we bypass it entirely and render a small
   * custom red-bordered block instead.
   */
  flaggedComment?: string;
}

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  title,
  subtitle,
  errorMessage,
  documentType,
  onUpload,
  onDelete,
  fileUrl,
  fileName,
  disabled,
  flaggedComment,
}) => {
  const { getTranslation } = useContext(I18nContext);

  const handleUpload = async (file: File): Promise<void> => {
    await onUpload(file, documentType);
  };

  const handleDelete = async (): Promise<void> => {
    await onDelete();
  };

  const isFlaggedUploadedFile = !!flaggedComment && !!fileUrl && !!fileName;

  return (
    <WhmStack
      sx={{
        display: "flex",
        height: "100%",
      }}
    >
      <WhmStack sx={{ marginBottom: "8px" }}>
        <WhmTypography variant="body1">{title}</WhmTypography>
        <WhmTypography
          sx={{
            minHeight: { xs: "0", lg: "60px", xl: "40px" },
            color: whmTheme.palette.text.secondary,
          }}
          variant="body2"
        >
          {subtitle}
        </WhmTypography>
      </WhmStack>
      <div>
        {isFlaggedUploadedFile ? (
          <WhmStack sx={{ gap: "8px" }}>
            <WhmStack
              direction="row"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
              gap="8px"
              sx={{
                border: "1px dashed",
                borderColor: whmTheme.palette.error.main,
                borderRadius: "8px",
                padding: "16px",
              }}
            >
              <a href={fileUrl} target="_blank" rel="noreferrer">
                <WhmTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: whmTheme.palette.text.primary,
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {fileName}
                </WhmTypography>
              </a>
              <WhmButton
                variant="outlined"
                size="small"
                disabled={disabled}
                onClick={() => void handleDelete()}
                sx={{ textTransform: "none", flexShrink: 0 }}
              >
                {getTranslation(
                  translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_CTA_REMOVE,
                )}
              </WhmButton>
            </WhmStack>
            <WhmBox
              sx={{
                backgroundColor: "rgba(211, 47, 47, 0.06)",
                borderRadius: "8px",
                padding: "12px",
              }}
            >
              <WhmTypography
                variant="body2"
                sx={{ color: whmTheme.palette.text.primary }}
              >
                {flaggedComment}
              </WhmTypography>
            </WhmBox>
          </WhmStack>
        ) : (
          <WhmDropzone
            disabled={disabled}
            title={getTranslation(
              translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_TITLE,
            )}
            preview={
              fileUrl && fileName ? { url: fileUrl, fileName } : undefined
            }
            onValidate={(file: File) => {
              if (file.size > 50 * 1024 * 1024) {
                return {
                  error: getTranslation(
                    TranslationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_INVALID_SIZE_ERROR,
                  ),
                };
              }

              return undefined;
            }}
            hideCancelButtonWhenLoading
            uploadButtonText={getTranslation(
              translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_CTA,
            )}
            description={getTranslation(
              translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_SUBTITLE,
            )}
            deleteButtonText={getTranslation(
              translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_CTA_REMOVE,
            )}
            uploadingCancelText={getTranslation(
              translationKeys.PROFILE_DOCUMENTS_ENTITY_UPLOADER_CTA_CANCEL,
            )}
            onUpload={handleUpload}
            onDelete={handleDelete}
            required={!disabled}
            uploadingErrorText={errorMessage}
          />
        )}
      </div>
    </WhmStack>
  );
};
